import { useEffect, useState, createElement } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { DEFAULT_CONTENT, mergeContent } from "@/lib/content";

const GROUPS = [
  { id: "site", label: "Business Info & Header/Footer", path: ["site"], hint: "Logo, contact information, business hours, social links, header button and footer text." },
  { id: "home", label: "Homepage", path: ["home"], hint: "Section order & visibility, hero, services, how it works, featured, corporate callout, testimonials." },
  { id: "pages", label: "Other Pages", path: ["pages"], hint: "Headings, descriptions, images and buttons on each public page." },
  { id: "policies", label: "Policy Pages", path: ["policies"], hint: "Refund, Terms, Privacy and Studio Safety pages (footer links). Booking checkout policies live under Settings." },
];

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isImageKey = (k) => /(^|_)(image|img|logo_image|photo)$/.test(k);
const isLongKey = (k) => /(description|body|tagline|hours|footnote)$/.test(k);
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

function Field({ k, v, onChange, path }) {
  const tid = `content-${path.join("-")}`;
  if (typeof v === "boolean") return (
    <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={v} onChange={(e) => onChange(e.target.checked)} data-testid={tid}/> {label(k)}</label>
  );
  if (typeof v === "number") return (
    <label className="text-xs block">{label(k)}<input type="number" value={v} onChange={(e) => onChange(Number(e.target.value))} data-testid={tid} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/></label>
  );
  if (isImageKey(k)) return (
    <div className="text-xs md:col-span-2">{label(k)}<div className="mt-1"><ImageUpload value={v} onChange={onChange} testid={tid}/></div></div>
  );
  if (isLongKey(k)) return (
    <label className="text-xs block md:col-span-2">{label(k)}<textarea rows={k === "body" ? 8 : 3} value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/></label>
  );
  return (
    <label className="text-xs block">{label(k)}<input value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/></label>
  );
}

function SectionsOrder({ items, onChange }) {
  const move = (i, d) => { const c = [...items]; const j = i + d; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div className="space-y-1.5" data-testid="content-home-sections">
      <div className="text-xs text-charcoal-muted">Reorder or hide homepage sections.</div>
      {items.map((s, i) => (
        <div key={s.id} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${s.hidden ? "bg-cream-alt/60 text-charcoal-muted" : "bg-white"}`} data-testid={`section-row-${s.id}`}>
          <span className="flex-1 font-medium">{s.label}</span>
          <button type="button" onClick={() => move(i, -1)} data-testid={`section-up-${s.id}`} className="p-1 rounded hover:bg-cream-alt"><ArrowUp size={14}/></button>
          <button type="button" onClick={() => move(i, 1)} data-testid={`section-down-${s.id}`} className="p-1 rounded hover:bg-cream-alt"><ArrowDown size={14}/></button>
          <button type="button" onClick={() => onChange(items.map((x, j) => (j === i ? { ...x, hidden: !x.hidden } : x)))} data-testid={`section-toggle-${s.id}`} className={`ml-1 px-2 py-1 rounded-full text-[11px] inline-flex items-center gap-1 ${s.hidden ? "bg-cream-alt" : "bg-forest/10 text-forest"}`}>{s.hidden ? <><EyeOff size={12}/> Hidden</> : <><Eye size={12}/> Visible</>}</button>
        </div>
      ))}
    </div>
  );
}

function ListEditor({ k, items, onChange, path }) {
  const template = items[0] ? Object.fromEntries(Object.keys(items[0]).map((key) => [key, typeof items[0][key] === "boolean" ? false : ""])) : { title: "", description: "" };
  const patch = (i, val) => onChange(items.map((it, j) => (j === i ? val : it)));
  const move = (i, d) => { const c = [...items]; const j = i + d; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div className="md:col-span-2 rounded-2xl bg-cream-alt/50 p-3" data-testid={`content-list-${path.join("-")}`}>
      <div className="flex items-center justify-between"><div className="text-xs font-semibold">{label(k)} ({items.length})</div>
        <button type="button" onClick={() => onChange([...items, template])} data-testid={`content-list-add-${path.join("-")}`} className="text-[11px] rounded-full bg-terracotta text-white px-3 py-1 inline-flex items-center gap-1"><Plus size={11}/> Add</button></div>
      <div className="mt-2 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="bg-white rounded-xl p-3">
            <div className="flex items-center justify-between mb-2 text-[11px] text-charcoal-muted"><span>#{i + 1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} className="p-1 rounded hover:bg-cream-alt"><ArrowUp size={12}/></button>
                <button type="button" onClick={() => move(i, 1)} className="p-1 rounded hover:bg-cream-alt"><ArrowDown size={12}/></button>
                <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`content-list-remove-${path.join("-")}-${i}`} className="p-1 rounded text-red-600 hover:bg-red-50"><Trash2 size={12}/></button>
              </div></div>
            {createElement(ObjectEditor, { value: it, onChange: (val) => patch(i, val), path: [...path, String(i)] })}
          </div>
        ))}
      </div>
    </div>
  );
}

function ObjectEditor({ value, onChange, path }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {Object.entries(value).map(([k, v]) => {
        const p = [...path, k];
        if (k === "sections" && Array.isArray(v)) return <div key={k} className="md:col-span-2"><SectionsOrder items={v} onChange={(nv) => set(k, nv)}/></div>;
        if (Array.isArray(v)) return <ListEditor key={k} k={k} items={v} onChange={(nv) => set(k, nv)} path={p}/>;
        if (isObj(v)) return <Collapsible key={k} title={label(k)} path={p}>{createElement(ObjectEditor, { value: v, onChange: (nv) => set(k, nv), path: p })}</Collapsible>;
        return <Field key={k} k={k} v={v} onChange={(nv) => set(k, nv)} path={p}/>;
      })}
    </div>
  );
}

function Collapsible({ title, children, path }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:col-span-2 rounded-2xl border border-cream-alt bg-white">
      <button type="button" onClick={() => setOpen(!open)} data-testid={`content-toggle-${path.join("-")}`} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold">{title}{open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}</button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default function ContentTab() {
  const [content, setContent] = useState(null);
  const [group, setGroup] = useState("site");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setContent(mergeContent(DEFAULT_CONTENT, r.data.site_content || {}))); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/site_content", { value: content }); toast.success("Website content saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const reset = () => { if (!window.confirm("Reset this section to the original website text and images?")) return; setContent({ ...content, [group]: DEFAULT_CONTENT[group] }); };
  if (!content) return <div>Loading…</div>;
  const g = GROUPS.find((x) => x.id === group);
  return (
    <div data-testid="content-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold">Website Content</h1>
          <p className="text-charcoal-muted text-sm">Edit public text, images, buttons and section visibility. Prices, rug sizes, add-ons and booking policies are under <b>Settings</b>.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={reset} data-testid="content-reset-btn" className="rounded-full bg-cream-alt px-4 py-2 text-sm font-semibold">Reset Section</button>
          <button type="button" onClick={save} disabled={saving} data-testid="content-save-btn" className="rounded-full bg-terracotta text-white px-5 py-2.5 font-semibold shadow-button disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {GROUPS.map((x) => <button key={x.id} type="button" onClick={() => setGroup(x.id)} data-testid={`content-group-${x.id}`} className={`px-4 py-2 rounded-full text-sm font-medium ${group === x.id ? "bg-terracotta text-white" : "bg-white text-charcoal hover:bg-cream-alt"}`}>{x.label}</button>)}
      </div>
      <div className="bg-white rounded-3xl p-6 shadow-card">
        <p className="text-xs text-charcoal-muted mb-4">{g.hint}</p>
        <ObjectEditor value={content[group]} onChange={(v) => setContent({ ...content, [group]: v })} path={[group]}/>
      </div>
      <div className="mt-4 flex justify-end"><button type="button" onClick={save} disabled={saving} data-testid="content-save-btn-bottom" className="rounded-full bg-terracotta text-white px-5 py-2.5 font-semibold shadow-button disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button></div>
    </div>
  );
}
